import {
	type BillingCurrency,
	type BillingRecord,
	DEFAULT_COMPANY_RETENTION_DAYS,
	type SipCredentialItem,
} from "@repo/common";
import {
	billingRecordsCollection,
	createBillingRecord,
	createSubscription,
	db,
	deleteBillingRecord,
	functions,
	getInvoiceDownloadUrl,
	httpsCallable,
	logAction,
	updateBillingRecord,
	updateCompany,
	updateSubscription,
	useAuth,
} from "@repo/ui";
import { deleteField, onSnapshot, orderBy, query } from "firebase/firestore";
import {
	ArrowLeft,
	Copy,
	Download,
	Edit,
	Globe2,
	Key,
	Loader2,
	Plus,
	RefreshCw,
	Shield,
	Trash2,
	UserPlus,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Company, Service, Subscription } from "../types";
import { generateSipPassword } from "../utils/generateSipPassword";
import { AdminCallStats } from "./AdminCallStats";
import { CompanyExternalExecutionPolicyCard } from "./CompanyExternalExecutionPolicyCard";
import { CompanyGoogleCalendarAvailabilityCard } from "./CompanyGoogleCalendarAvailabilityCard";
import { CompanyGrokbotAvailabilityCard } from "./CompanyGrokbotAvailabilityCard";
import { CustomerTypeSelect } from "./CustomerTypeSelect";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

type FeatureOverride = "inherit" | "enabled" | "disabled";
type OverrideFeatureKey = Exclude<
	keyof NonNullable<Company["features"]>,
	"apiAccess"
>;

const capabilityFields: { key: OverrideFeatureKey; label: string }[] = [
	{ key: "callerMemoryUserUi", label: "Caller Memory: User UI" },
	{
		key: "callerMemoryAgentInjection",
		label: "Caller Memory: Agent Injection",
	},
	{ key: "outboundPlaybookCalls", label: "Outbound Playbook Calls" },
	{ key: "elevenLabsProvider", label: "ElevenLabs Provider" },
];

const retentionFields: { key: OverrideFeatureKey; label: string }[] = [
	{ key: "piiRemoval", label: "PII Removal & Redaction" },
	{ key: "deleteAudioFromTwilio", label: "Delete Audio from Twilio" },
	{ key: "deletePiiJsonFromGcp", label: "Delete pii.json from GCP" },
];

const callRetentionOptions = [
	{ value: "30", label: "30 days" },
	{ value: "60", label: "60 days" },
	{ value: "90", label: "90 days" },
	{ value: "120", label: "120 days" },
	{ value: "180", label: "180 days" },
	{ value: "365", label: "1 year" },
	{ value: "730", label: "2 years" },
	{ value: "1095", label: "3 years" },
	{ value: "never", label: "Does not expire" },
] as const;

function callRetentionValue(days?: number | null): string {
	return days === null
		? "never"
		: String(days ?? DEFAULT_COMPANY_RETENTION_DAYS);
}

function callRetentionLabel(days?: number | null): string {
	return (
		callRetentionOptions.find(({ value }) => value === callRetentionValue(days))
			?.label ?? `${days} days`
	);
}

const booleanToOverride = (value?: boolean): FeatureOverride =>
	value === undefined ? "inherit" : value ? "enabled" : "disabled";

interface CompanyDetailsPageProps {
	companyId: string;
	initiallyEditing?: boolean;
	onNavigateBack: () => void;
	onNavigateToService: (serviceId: string) => void;
	onAddService: () => void;
	onAddUser: () => void;
	onNavigateToOnboarding: (onboardingId: string) => void;
}

export function CompanyDetailsPage({
	companyId,
	...navigationProps
}: CompanyDetailsPageProps) {
	const { companies } = useData();
	const company = companies.find((candidate) => candidate.id === companyId);

	if (!company) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" onClick={navigationProps.onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Companies
					</Button>
				</div>
				<Card>
					<CardContent className="py-8">
						<p className="text-center text-slate-500">Company not found</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<LoadedCompanyDetailsPage
			companyId={companyId}
			company={company}
			{...navigationProps}
		/>
	);
}

interface LoadedCompanyDetailsPageProps extends CompanyDetailsPageProps {
	company: Company;
}

function LoadedCompanyDetailsPage({
	companyId,
	company,
	initiallyEditing = false,
	onNavigateBack,
	onNavigateToService,
	onAddService,
	onAddUser,
	onNavigateToOnboarding,
}: LoadedCompanyDetailsPageProps) {
	const { users, services, subscriptions, dailyCallStats } = useData();
	const { user } = useAuth();
	const recordAudit = async (
		type:
			| "subscription_created"
			| "subscription_updated"
			| "billing_record_created"
			| "billing_record_updated"
			| "billing_record_deleted",
		data: Record<string, unknown>,
	) => {
		if (!user?.uid) return;
		try {
			await logAction(db, {
				userId: user.uid,
				type,
				companyId,
				data,
			});
		} catch (error) {
			console.error("Failed to write subscription audit log", error);
			toast.warning(
				"The change was saved, but its audit log was not recorded.",
			);
		}
	};

	const companyStats = useMemo(
		() => dailyCallStats.filter((s) => s.companyId === companyId),
		[dailyCallStats, companyId],
	);

	const companyUsers = useMemo(
		() => users.filter((u) => u.companyId === companyId),
		[users, companyId],
	);

	const companyServices = useMemo(
		() => services.filter((s) => s.companyId === companyId),
		[services, companyId],
	);

	const [isEditing, setIsEditing] = useState(initiallyEditing);
	const [editedCompany, setEditedCompany] = useState(company);
	const [activeTab, setActiveTab] = useState("profile");

	// Subscription & Billing States
	const companySubscriptions = useMemo(
		() => subscriptions.filter((s) => s.companyId === companyId),
		[subscriptions, companyId],
	);
	const subscription = useMemo(
		() =>
			companySubscriptions.find((candidate) => candidate.status !== "canceled"),
		[companySubscriptions],
	);

	const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
	const [billingRecordsLoading, setBillingRecordsLoading] = useState(false);

	// Load billing records
	useEffect(() => {
		if (!subscription?.id) {
			setBillingRecords([]);
			return;
		}
		setBillingRecordsLoading(true);
		const q = query(
			billingRecordsCollection(db, subscription.id),
			orderBy("createdAt", "desc"),
		);
		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const records = snapshot.docs.map((doc) => doc.data());
				setBillingRecords(records);
				setBillingRecordsLoading(false);
			},
			(error) => {
				console.error("Failed to load billing records", error);
				setBillingRecordsLoading(false);
			},
		);
		return () => unsubscribe();
	}, [subscription?.id]);

	// Subscription Dialog States
	const [showSubDialog, setShowSubDialog] = useState(false);
	const [isEditingSub, setIsEditingSub] = useState(false);
	const [subPlanName, setSubPlanName] = useState("Starter");
	const [subStatus, setSubStatus] = useState<Subscription["status"]>("active");
	const [subPrice, setSubPrice] = useState(490);
	const [subCurrency, setSubCurrency] = useState<BillingCurrency>("ILS");
	const [subBillingInterval, setSubBillingInterval] =
		useState<Subscription["billingInterval"]>("monthly");
	const [subNextBillingDate, setSubNextBillingDate] = useState("");
	const [subCancelAtPeriodEnd, setSubCancelAtPeriodEnd] = useState(false);
	const [subBillingProvider, setSubBillingProvider] = useState("manual");

	const handleOpenEditSub = () => {
		if (!subscription) return;
		setSubPlanName(subscription.planName);
		setSubStatus(subscription.status);
		setSubPrice(subscription.price);
		setSubCurrency(subscription.currency);
		setSubBillingInterval(subscription.billingInterval);
		setSubNextBillingDate(
			subscription.nextBillingDate
				? new Date(subscription.nextBillingDate).toISOString().split("T")[0]
				: "",
		);
		setSubCancelAtPeriodEnd(subscription.cancelAtPeriodEnd);
		setSubBillingProvider(subscription.billingProvider);
		setIsEditingSub(true);
		setShowSubDialog(true);
	};

	const handleOpenCreateSub = () => {
		if (subscription) {
			toast.error("This company already has a non-canceled subscription.");
			return;
		}
		setSubPlanName("Starter");
		setSubStatus("active");
		setSubPrice(490);
		setSubCurrency("ILS");
		setSubBillingInterval("monthly");
		const oneMonthFromNow = new Date();
		oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
		setSubNextBillingDate(oneMonthFromNow.toISOString().split("T")[0]);
		setSubCancelAtPeriodEnd(false);
		setSubBillingProvider("manual");
		setIsEditingSub(false);
		setShowSubDialog(true);
	};

	const handleSaveSub = async () => {
		const subData: Omit<Subscription, "id" | "createdAt" | "updatedAt"> = {
			companyId,
			planName: subPlanName,
			status: subStatus,
			price: Number(subPrice),
			currency: subCurrency,
			billingInterval: subBillingInterval,
			nextBillingDate:
				subStatus !== "canceled" && subNextBillingDate
					? new Date(subNextBillingDate).toISOString()
					: null,
			cancelAtPeriodEnd:
				subStatus === "canceled" ? false : subCancelAtPeriodEnd,
			cancellationRequestedAt:
				subStatus !== "canceled" && subCancelAtPeriodEnd
					? subscription?.cancellationRequestedAt || new Date().toISOString()
					: null,
			canceledAt:
				subStatus === "canceled"
					? subscription?.canceledAt || new Date().toISOString()
					: null,
			billingProvider: subBillingProvider,
		};

		try {
			if (isEditingSub && subscription?.id) {
				await updateSubscription(db, subscription.id, subData);
				await recordAudit("subscription_updated", {
					subscriptionId: subscription.id,
				});
			} else {
				const subscriptionId = await createSubscription(db, subData);
				await recordAudit("subscription_created", { subscriptionId });
			}
			setShowSubDialog(false);
		} catch (error) {
			console.error("Failed to save subscription", error);
			toast.error(
				error instanceof Error ? error.message : "Failed to save subscription",
			);
		}
	};

	// Billing Record Dialog States
	const [showRecordDialog, setShowRecordDialog] = useState(false);
	const [editingRecord, setEditingRecord] = useState<BillingRecord | null>(
		null,
	);
	const [recPeriodStart, setRecPeriodStart] = useState("");
	const [recPeriodEnd, setRecPeriodEnd] = useState("");
	const [recDescription, setRecDescription] = useState("");
	const [recAmount, setRecAmount] = useState(490);
	const [recTaxAmount, setRecTaxAmount] = useState(75);
	const [recTotalAmount, setRecTotalAmount] = useState(565);
	const [recCurrency, setRecCurrency] = useState<BillingCurrency>("ILS");
	const [recStatus, setRecStatus] = useState<BillingRecord["status"]>("paid");
	const [recInvoiceUrl, setRecInvoiceUrl] = useState("");
	const [recInvoiceNumber, setRecInvoiceNumber] = useState("");
	const [recPaymentPaidAt, setRecPaymentPaidAt] = useState("");
	const [recPaymentMethod, setRecPaymentMethod] = useState<
		"manual" | "credit_card" | "bank_transfer"
	>("manual");
	const [recPaymentProvider, setRecPaymentProvider] = useState<
		"manual" | "grow"
	>("manual");
	const [recPaymentExternalId, setRecPaymentExternalId] = useState("");
	const [recSource, setRecSource] = useState<"manual" | "grow">("manual");

	const handleRecAmountChange = (val: number) => {
		setRecAmount(val);
		setRecTotalAmount(val + recTaxAmount);
	};

	const handleRecTaxChange = (val: number) => {
		setRecTaxAmount(val);
		setRecTotalAmount(recAmount + val);
	};

	const handleOpenCreateRecord = () => {
		if (!subscription) return;
		setEditingRecord(null);
		setRecPeriodStart(new Date().toISOString().split("T")[0]);
		setRecPeriodEnd(new Date().toISOString().split("T")[0]);
		setRecDescription(
			`${subscription.planName} plan - ${new Date().toLocaleString("default", { month: "long", year: "numeric" })}`,
		);
		setRecAmount(subscription.price);
		setRecTaxAmount(Math.round(subscription.price * 0.17));
		setRecTotalAmount(
			subscription.price + Math.round(subscription.price * 0.17),
		);
		setRecCurrency(subscription.currency);
		setRecStatus("paid");
		setRecInvoiceUrl("");
		setRecInvoiceNumber("");
		setRecPaymentPaidAt(new Date().toISOString().split("T")[0]);
		setRecPaymentMethod("manual");
		setRecPaymentProvider("manual");
		setRecPaymentExternalId("");
		setRecSource("manual");
		setShowRecordDialog(true);
	};

	const handleOpenEditRecord = (record: BillingRecord) => {
		setEditingRecord(record);
		setRecPeriodStart(
			record.periodStart
				? new Date(record.periodStart).toISOString().split("T")[0]
				: "",
		);
		setRecPeriodEnd(
			record.periodEnd
				? new Date(record.periodEnd).toISOString().split("T")[0]
				: "",
		);
		setRecDescription(record.description);
		setRecAmount(record.amount);
		setRecTaxAmount(record.taxAmount);
		setRecTotalAmount(record.totalAmount);
		setRecCurrency(record.currency);
		setRecStatus(record.status);
		setRecInvoiceUrl(record.invoiceUrl || "");
		setRecInvoiceNumber(record.invoiceNumber || "");
		setRecPaymentPaidAt(
			record.payment?.paidAt
				? new Date(record.payment.paidAt).toISOString().split("T")[0]
				: "",
		);
		setRecPaymentMethod(record.payment?.method || "manual");
		setRecPaymentProvider(record.payment?.provider || "manual");
		setRecPaymentExternalId(record.payment?.externalPaymentId || "");
		setRecSource(record.source);
		setShowRecordDialog(true);
	};

	const handleSaveRecord = async () => {
		if (!subscription?.id) return;
		const recordData: Omit<BillingRecord, "id" | "createdAt" | "updatedAt"> = {
			companyId,
			subscriptionId: subscription.id,
			periodStart: recPeriodStart
				? new Date(recPeriodStart).toISOString()
				: null,
			periodEnd: recPeriodEnd ? new Date(recPeriodEnd).toISOString() : null,
			description: recDescription,
			amount: Number(recAmount),
			taxAmount: Number(recTaxAmount),
			totalAmount: Number(recTotalAmount),
			currency: recCurrency,
			status: recStatus,
			invoiceUrl: recInvoiceUrl || null,
			invoiceNumber: recInvoiceNumber || null,
			payment:
				recStatus === "paid"
					? {
							paidAt: recPaymentPaidAt
								? new Date(recPaymentPaidAt).toISOString()
								: new Date().toISOString(),
							method: recPaymentMethod,
							provider: recPaymentProvider,
							externalPaymentId: recPaymentExternalId || null,
						}
					: null,
			source: recSource,
		};

		try {
			if (editingRecord?.id) {
				await updateBillingRecord(
					db,
					subscription.id,
					editingRecord.id,
					recordData,
				);
				await recordAudit("billing_record_updated", {
					subscriptionId: subscription.id,
					billingRecordId: editingRecord.id,
				});
			} else {
				const billingRecordId = await createBillingRecord(
					db,
					subscription.id,
					recordData,
				);
				await recordAudit("billing_record_created", {
					subscriptionId: subscription.id,
					billingRecordId,
				});
			}
			setShowRecordDialog(false);
		} catch (error) {
			console.error("Failed to save billing record", error);
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to save billing record",
			);
		}
	};

	const [recordToDelete, setRecordToDelete] = useState<BillingRecord | null>(
		null,
	);

	const handleDeleteRecord = async () => {
		if (!subscription?.id || !recordToDelete?.id) return;
		try {
			await deleteBillingRecord(db, subscription.id, recordToDelete.id);
			await recordAudit("billing_record_deleted", {
				subscriptionId: subscription.id,
				billingRecordId: recordToDelete.id,
			});
			setRecordToDelete(null);
		} catch (error) {
			console.error("Failed to delete billing record", error);
		}
	};

	const [downloadingRecordId, setDownloadingRecordId] = useState<string | null>(
		null,
	);

	const handleDownloadInvoice = async (record: BillingRecord) => {
		if (!subscription?.id || !record.id) return;
		setDownloadingRecordId(record.id);
		try {
			const result = await getInvoiceDownloadUrl({
				companyId,
				subscriptionId: subscription.id,
				billingRecordId: record.id,
			});
			if (result.data?.url) {
				window.open(result.data.url, "_blank");
				toast.success("Invoice download URL generated successfully");
			} else {
				throw new Error("No URL returned");
			}
		} catch (error) {
			console.error("Failed to generate download URL", error);
			toast.error("Failed to generate download URL");
		} finally {
			setDownloadingRecordId(null);
		}
	};

	const handleSave = async () => {
		if (!editedCompany) return;
		if (
			[
				editedCompany.name,
				editedCompany.contactName,
				editedCompany.contactEmail,
				editedCompany.contactPhone,
			].some((value) => !value.trim())
		) {
			toast.error("Company name and contact fields are required");
			return;
		}
		const featureValue = (value?: boolean) =>
			value === undefined ? deleteField() : value;
		try {
			await updateCompany(db, companyId, {
				name: editedCompany.name,
				customerType: editedCompany.customerType ?? null,
				contactName: editedCompany.contactName,
				contactEmail: editedCompany.contactEmail,
				contactPhone: editedCompany.contactPhone,
				retentionDays:
					editedCompany.retentionDays === undefined
						? DEFAULT_COMPANY_RETENTION_DAYS
						: editedCompany.retentionDays,
				...(editedCompany.sipSettings
					? { sipSettings: editedCompany.sipSettings }
					: {}),
				"features.callerMemoryUserUi": featureValue(
					editedCompany.features?.callerMemoryUserUi,
				),
				"features.callerMemoryAgentInjection": featureValue(
					editedCompany.features?.callerMemoryAgentInjection,
				),
				"features.outboundPlaybookCalls": featureValue(
					editedCompany.features?.outboundPlaybookCalls,
				),
				"features.elevenLabsProvider": featureValue(
					editedCompany.features?.elevenLabsProvider,
				),
				"features.apiAccess": editedCompany.features?.apiAccess === true,
				"features.piiRemoval": featureValue(editedCompany.features?.piiRemoval),
				"features.deleteAudioFromTwilio": featureValue(
					editedCompany.features?.deleteAudioFromTwilio,
				),
				"features.deletePiiJsonFromGcp": featureValue(
					editedCompany.features?.deletePiiJsonFromGcp,
				),
			} as Partial<Company>);
			toast.success("Company updated successfully");
			setIsEditing(false);
		} catch (error) {
			console.error("Failed to update company", error);
			toast.error("Failed to update company");
		}
	};

	const handleCancel = () => {
		setEditedCompany(company);
		setIsEditing(false);
	};

	const handleStartEditing = () => {
		setEditedCompany(company);
		setIsEditing(true);
	};

	const renderFeatureOverride = (key: OverrideFeatureKey, label: string) => {
		const value = isEditing
			? editedCompany.features?.[key]
			: company.features?.[key];
		const override = booleanToOverride(value);
		return (
			<div key={key} className="space-y-2">
				<Label htmlFor={isEditing ? `company-feature-${key}` : undefined}>
					{label}
				</Label>
				{isEditing ? (
					<Select
						value={override}
						onValueChange={(nextOverride: FeatureOverride) =>
							setEditedCompany((current) => ({
								...current,
								features: {
									...current.features,
									[key]:
										nextOverride === "inherit"
											? undefined
											: nextOverride === "enabled",
								},
							}))
						}
					>
						<SelectTrigger id={`company-feature-${key}`}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="inherit">Use system default</SelectItem>
							<SelectItem value="enabled">Enabled for company</SelectItem>
							<SelectItem value="disabled">Disabled for company</SelectItem>
						</SelectContent>
					</Select>
				) : (
					<div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
						{override === "inherit"
							? "Use system default"
							: override === "enabled"
								? "Enabled for company"
								: "Disabled for company"}
					</div>
				)}
			</div>
		);
	};

	// SIP Credentials States & Handlers
	const [sipCredentials, setSipCredentials] = useState<SipCredentialItem[]>([]);
	const [isLoadingSipCredentials, setIsLoadingSipCredentials] = useState(false);
	const [isAddSipUserOpen, setIsAddSipUserOpen] = useState(false);
	const [newSipUsername, setNewSipUsername] = useState("");
	const [newSipPassword, setNewSipPassword] = useState("");
	const [isSubmittingSipUser, setIsSubmittingSipUser] = useState(false);
	const [deletingSipCredential, setDeletingSipCredential] =
		useState<SipCredentialItem | null>(null);
	const [isDeletingSipUser, setIsDeletingSipUser] = useState(false);

	const loadSipCredentials = useCallback(async () => {
		if (!company?.sipSettings?.credentialListSid) return;
		setIsLoadingSipCredentials(true);
		try {
			const listCallable = httpsCallable<
				{ companyId: string },
				{ credentials: SipCredentialItem[] }
			>(functions, "listCompanySipCredentials");
			const res = await listCallable({ companyId });
			setSipCredentials(res.data.credentials || []);
		} catch (err) {
			console.error("Failed to load SIP credentials", err);
			toast.error(
				err instanceof Error
					? err.message
					: "Failed to load SIP credentials from Twilio.",
			);
		} finally {
			setIsLoadingSipCredentials(false);
		}
	}, [company?.sipSettings?.credentialListSid, companyId]);

	useEffect(() => {
		if (company?.sipSettings?.credentialListSid) {
			loadSipCredentials();
		} else {
			setSipCredentials([]);
		}
	}, [company?.sipSettings?.credentialListSid, loadSipCredentials]);

	const generateStrongPassword = () => {
		setNewSipPassword(generateSipPassword());
	};

	const handleCreateSipUser = async () => {
		if (!newSipUsername.trim() || !newSipPassword) {
			toast.error("Please provide both username and password.");
			return;
		}
		if (newSipPassword.length < 12) {
			toast.error("Password must be at least 12 characters.");
			return;
		}
		setIsSubmittingSipUser(true);
		try {
			const createCallable = httpsCallable(
				functions,
				"createCompanySipCredential",
			);
			await createCallable({
				companyId,
				username: newSipUsername.trim(),
				password: newSipPassword,
			});
			toast.success(`SIP User ${newSipUsername} created in Twilio.`);
			setIsAddSipUserOpen(false);
			setNewSipUsername("");
			setNewSipPassword("");
			await loadSipCredentials();
		} catch (err) {
			console.error("Failed to create SIP credential", err);
			toast.error(
				err instanceof Error ? err.message : "Failed to create SIP credential.",
			);
		} finally {
			setIsSubmittingSipUser(false);
		}
	};

	const handleDeleteSipUser = async () => {
		if (!deletingSipCredential) return;
		setIsDeletingSipUser(true);
		try {
			const deleteCallable = httpsCallable(
				functions,
				"deleteCompanySipCredential",
			);
			await deleteCallable({
				companyId,
				credentialSid: deletingSipCredential.sid,
				username: deletingSipCredential.username,
			});
			toast.success(`SIP User ${deletingSipCredential.username} deleted.`);
			setDeletingSipCredential(null);
			await loadSipCredentials();
		} catch (err) {
			console.error("Failed to delete SIP credential", err);
			toast.error(
				err instanceof Error ? err.message : "Failed to delete SIP credential.",
			);
		} finally {
			setIsDeletingSipUser(false);
		}
	};

	const formatTimestamp = (timestamp: Date | string | null | undefined) => {
		return new Date(timestamp ?? "").toLocaleString();
	};
	const onboardingId = company.onboarding?.onboardingId;

	return (
		<div className={isEditing ? "space-y-6 pb-20" : "space-y-6"}>
			<div className="admin-company-header flex flex-wrap items-end justify-between gap-4">
				<div className="space-y-2">
					<Button variant="ghost" onClick={onNavigateBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Companies
					</Button>
					<h1>{company.name}</h1>
					<p className="text-sm text-muted-foreground">
						Company profile, platform capabilities, and operational records
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					{isEditing ? (
						<>
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
							<Button onClick={handleSave}>Save Changes</Button>
						</>
					) : (
						<>
							{onboardingId && (
								<Button
									variant="outline"
									onClick={() => onNavigateToOnboarding(onboardingId)}
								>
									<UserPlus className="h-4 w-4 mr-2" />
									Open Onboarding
								</Button>
							)}
							<Button onClick={handleStartEditing}>Edit Company</Button>
						</>
					)}
				</div>
			</div>

			<Tabs
				value={activeTab}
				onValueChange={setActiveTab}
				className="admin-company-tabs"
			>
				<div className="admin-company-tabs-nav">
					<TabsList
						aria-label="Company sections"
						className="admin-company-tabs-list"
					>
						<TabsTrigger value="profile">Profile</TabsTrigger>
						<TabsTrigger value="platform">Platform</TabsTrigger>
						<TabsTrigger value="voice">Voice</TabsTrigger>
						<TabsTrigger value="operations">Operations</TabsTrigger>
						<TabsTrigger value="billing">Billing</TabsTrigger>
					</TabsList>
				</div>
				<TabsContent
					forceMount
					value="profile"
					hidden={activeTab !== "profile"}
				>
					<Card>
						<CardHeader>
							<CardTitle>Company Information</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-4 sm:grid-cols-2">
								<div>
									<Label>Company ID</Label>
									<Input value={company.id} disabled className="mt-1" />
								</div>
								<div>
									<Label>Company Name</Label>
									<Input
										value={isEditing ? editedCompany?.name : company.name}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												name: e.target.value,
											})
										}
										disabled={!isEditing}
										className="mt-1"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="company-customer-type">Customer type</Label>
									<CustomerTypeSelect
										id="company-customer-type"
										value={
											isEditing
												? editedCompany?.customerType
												: company.customerType
										}
										disabled={!isEditing}
										onChange={(customerType) =>
											setEditedCompany({ ...editedCompany!, customerType })
										}
									/>
								</div>
								<div>
									<Label>Contact Person</Label>
									<Input
										value={
											isEditing
												? editedCompany?.contactName
												: company.contactName
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												contactName: e.target.value,
											})
										}
										disabled={!isEditing}
										className="mt-1"
									/>
								</div>
								<div>
									<Label>Contact Email</Label>
									<Input
										value={
											isEditing
												? editedCompany?.contactEmail
												: company.contactEmail
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												contactEmail: e.target.value,
											})
										}
										disabled={!isEditing}
										className="mt-1"
									/>
								</div>
								<div>
									<Label>Contact Phone</Label>
									<Input
										value={
											isEditing
												? editedCompany?.contactPhone
												: company.contactPhone
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												contactPhone: e.target.value,
											})
										}
										disabled={!isEditing}
										className="mt-1"
									/>
								</div>
								<div>
									<Label>Created Date</Label>
									<Input
										value={formatTimestamp(company.createdAt)}
										disabled
										className="mt-1"
									/>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent
					forceMount
					value="platform"
					hidden={activeTab !== "platform"}
					className="space-y-6"
				>
					<Card>
						<CardHeader>
							<CardTitle>Platform settings</CardTitle>
							<p className="text-sm text-muted-foreground">
								Company overrides use the system default when set to inherit.
							</p>
						</CardHeader>
						<CardContent className="space-y-7">
							<section className="space-y-4">
								<h3 className="text-sm font-semibold text-foreground">
									Capabilities
								</h3>
								<div className="grid gap-4 sm:grid-cols-2">
									{capabilityFields.map(({ key, label }) =>
										renderFeatureOverride(key, label),
									)}
									<div className="space-y-2">
										<Label
											htmlFor={
												isEditing ? "company-feature-api-access" : undefined
											}
										>
											API Access
										</Label>
										{isEditing ? (
											<Select
												value={
													editedCompany.features?.apiAccess === true
														? "enabled"
														: "disabled"
												}
												onValueChange={(value) =>
													setEditedCompany((current) => ({
														...current,
														features: {
															...current.features,
															apiAccess: value === "enabled",
														},
													}))
												}
											>
												<SelectTrigger id="company-feature-api-access">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="enabled">
														Enabled for company
													</SelectItem>
													<SelectItem value="disabled">
														Disabled for company
													</SelectItem>
												</SelectContent>
											</Select>
										) : (
											<div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
												{company.features?.apiAccess === true
													? "Enabled for company"
													: "Disabled for company"}
											</div>
										)}
									</div>
								</div>
							</section>
							<section className="space-y-4 border-t border-border pt-6">
								<h3 className="text-sm font-semibold text-foreground">
									Privacy and retention
								</h3>
								<div className="space-y-2">
									<Label htmlFor="company-call-retention">
										Call transcripts and GCP audio
									</Label>
									{isEditing ? (
										<Select
											value={callRetentionValue(editedCompany?.retentionDays)}
											onValueChange={(value) =>
												setEditedCompany({
													...editedCompany!,
													retentionDays:
														value === "never" ? null : Number(value),
												})
											}
										>
											<SelectTrigger
												id="company-call-retention"
												className="w-full sm:max-w-xs"
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{callRetentionOptions.map(({ value, label }) => (
													<SelectItem key={value} value={value}>
														{label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : (
										<div className="text-sm text-foreground">
											{callRetentionLabel(company.retentionDays)}
										</div>
									)}
									<p className="text-xs text-muted-foreground">
										Applies to company call history and archived GCP recordings.
										Existing data requires a retention backfill.
									</p>
								</div>
								<div className="grid gap-4 sm:grid-cols-2">
									{retentionFields.map(({ key, label }) =>
										renderFeatureOverride(key, label),
									)}
								</div>
							</section>
						</CardContent>
					</Card>

					<CompanyGoogleCalendarAvailabilityCard companyId={companyId} />
					<CompanyGrokbotAvailabilityCard companyId={companyId} />
					<CompanyExternalExecutionPolicyCard
						key={companyId}
						companyId={companyId}
					/>
				</TabsContent>
				<TabsContent forceMount value="voice" hidden={activeTab !== "voice"}>
					{/* SIP Domain & Outbound Trunking Card */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Globe2 className="h-4 w-4" />
								SIP Domain & Trunking
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-4 lg:grid-cols-3">
								<div>
									<Label>SIP Domain URI / Name</Label>
									<Input
										value={
											isEditing
												? editedCompany?.sipSettings?.domainName || ""
												: company.sipSettings?.domainName || ""
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												sipSettings: {
													...editedCompany?.sipSettings,
													domainName: e.target.value,
												},
											})
										}
										placeholder="e.g. company.sip.twilio.com"
										disabled={!isEditing}
										className="mt-1 font-mono text-sm"
									/>
									<p className="text-xs text-slate-500 mt-1">
										Domain URI registered in Twilio SIP domain.
									</p>
								</div>
								<div>
									<Label>Twilio SIP Domain SID</Label>
									<Input
										value={
											isEditing
												? editedCompany?.sipSettings?.domainSid || ""
												: company.sipSettings?.domainSid || ""
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												sipSettings: {
													...editedCompany?.sipSettings,
													domainSid: e.target.value,
												},
											})
										}
										placeholder="SDxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
										disabled={!isEditing}
										className="mt-1 font-mono text-sm"
									/>
									<p className="text-xs text-slate-500 mt-1">
										Twilio SIP Domain Resource SID.
									</p>
								</div>
								<div>
									<Label>Twilio Credential List SID</Label>
									<Input
										value={
											isEditing
												? editedCompany?.sipSettings?.credentialListSid || ""
												: company.sipSettings?.credentialListSid || ""
										}
										onChange={(e) =>
											setEditedCompany({
												...editedCompany!,
												sipSettings: {
													...editedCompany?.sipSettings,
													credentialListSid: e.target.value,
												},
											})
										}
										placeholder="CLxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
										disabled={!isEditing}
										className="mt-1 font-mono text-sm"
									/>
									<p className="text-xs text-slate-500 mt-1">
										Associated Credential List SID in Twilio.
									</p>
								</div>
							</div>

							{company.sipSettings?.credentialListSid && (
								<div className="pt-4 border-t space-y-3">
									<div className="flex items-center justify-between">
										<div>
											<h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
												<Shield className="h-4 w-4 text-indigo-600" />
												SIP Credentials / Operators ({sipCredentials.length})
											</h4>
											<p className="text-xs text-slate-500">
												Operators in Twilio credential list assigned to this
												company.
											</p>
										</div>
										<div className="flex items-center gap-2">
											<Button
												variant="outline"
												size="sm"
												onClick={loadSipCredentials}
												disabled={isLoadingSipCredentials}
											>
												<RefreshCw
													className={`h-3.5 w-3.5 mr-1 ${
														isLoadingSipCredentials ? "animate-spin" : ""
													}`}
												/>
												Sync
											</Button>
											<Button
												size="sm"
												onClick={() => {
													setNewSipUsername("");
													generateStrongPassword();
													setIsAddSipUserOpen(true);
												}}
											>
												<Plus className="h-3.5 w-3.5 mr-1" />
												Add SIP User
											</Button>
										</div>
									</div>

									{isLoadingSipCredentials ? (
										<div className="flex items-center justify-center p-6 text-sm text-slate-500 gap-2">
											<Loader2 className="h-4 w-4 animate-spin" />
											Loading credentials from Twilio...
										</div>
									) : sipCredentials.length > 0 ? (
										<div className="border rounded-md overflow-hidden">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>SIP Username</TableHead>
														<TableHead>Credential SID</TableHead>
														<TableHead>Created</TableHead>
														<TableHead className="text-right">
															Actions
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{sipCredentials.map((cred) => (
														<TableRow key={cred.sid}>
															<TableCell className="font-medium font-mono text-sm">
																{cred.username}
															</TableCell>
															<TableCell className="font-mono text-xs text-slate-500">
																{cred.sid}
															</TableCell>
															<TableCell className="text-xs text-slate-500">
																{cred.dateCreated
																	? new Date(
																			cred.dateCreated,
																		).toLocaleDateString()
																	: "-"}
															</TableCell>
															<TableCell className="text-right">
																<Button
																	variant="ghost"
																	size="sm"
																	className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
																	onClick={() => setDeletingSipCredential(cred)}
																>
																	<Trash2 className="h-4 w-4" />
																</Button>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									) : (
										<div className="text-center py-6 border border-dashed rounded-md text-sm text-slate-500">
											No SIP credentials found in this Credential List. Click
											&quot;Add SIP User&quot; to create one.
										</div>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent
					forceMount
					value="operations"
					hidden={activeTab !== "operations"}
					className="space-y-6"
				>
					<AdminCallStats stats={companyStats} />

					<Card>
						<CardHeader className="flex flex-row items-center justify-between">
							<CardTitle>Services ({companyServices.length})</CardTitle>
							<Button size="sm" onClick={onAddService}>
								<Plus className="h-4 w-4 mr-2" />
								Add Service
							</Button>
						</CardHeader>
						<CardContent>
							{companyServices.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Service Name</TableHead>
											<TableHead>Agent Phone Number</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{companyServices.map((service) => (
											<TableRow key={service.id}>
												<TableCell>{service.name}</TableCell>
												<TableCell className="font-mono text-sm">
													{
														(service as Service & { agentPhoneNumber?: string })
															.agentPhoneNumber
													}
												</TableCell>
												<TableCell>
													<Badge
														variant={service.isActive ? "secondary" : "outline"}
													>
														{service.isActive ? "Active" : "Inactive"}
													</Badge>
												</TableCell>
												<TableCell>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onNavigateToService(service.id)}
													>
														View Details
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="text-center py-8 text-slate-500">
									No services configured for this company
								</div>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between">
							<CardTitle>Users ({companyUsers.length})</CardTitle>
							<Button size="sm" onClick={onAddUser}>
								<Plus className="h-4 w-4 mr-2" />
								Add User
							</Button>
						</CardHeader>
						<CardContent>
							{companyUsers.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Email</TableHead>
											<TableHead>Role</TableHead>
											<TableHead>Last Login</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{companyUsers.map((user) => (
											<TableRow key={user.id}>
												<TableCell>{user.email}</TableCell>
												<TableCell>
													<Badge
														variant={
															user.role === "admin" ? "default" : "secondary"
														}
													>
														{user.role}
													</Badge>
												</TableCell>
												<TableCell>{formatTimestamp(user.lastLogin)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="text-center py-8 text-slate-500">
									No users found for this company
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent
					forceMount
					value="billing"
					hidden={activeTab !== "billing"}
				>
					{/* Subscriptions & Billing */}
					<Card>
						<CardHeader className="flex flex-row items-center justify-between">
							<CardTitle>Subscription & Billing</CardTitle>
							{subscription ? (
								<Button size="sm" onClick={handleOpenEditSub}>
									<Edit className="h-4 w-4 mr-2" />
									Edit Subscription
								</Button>
							) : (
								<Button size="sm" onClick={handleOpenCreateSub}>
									<Plus className="h-4 w-4 mr-2" />
									Create Subscription
								</Button>
							)}
						</CardHeader>
						<CardContent className="space-y-6">
							{subscription ? (
								<div className="space-y-6">
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Plan Name
											</span>
											<span className="text-lg font-semibold text-slate-900">
												{subscription.planName}
											</span>
										</div>
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Status
											</span>
											<Badge
												variant={
													subscription.status === "active"
														? "secondary"
														: subscription.status === "trial"
															? "outline"
															: "destructive"
												}
												className="mt-1"
											>
												{subscription.status}
											</Badge>
										</div>
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Price
											</span>
											<span className="text-lg font-semibold text-slate-900">
												{subscription.price} {subscription.currency} /{" "}
												{subscription.billingInterval}
											</span>
										</div>
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Next Billing Date
											</span>
											<span className="text-sm font-medium text-slate-700">
												{subscription.nextBillingDate
													? new Date(
															subscription.nextBillingDate,
														).toLocaleDateString()
													: "—"}
											</span>
										</div>
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Billing Provider
											</span>
											<span className="text-sm font-medium text-slate-700 font-mono">
												{subscription.billingProvider}
											</span>
										</div>
										<div>
											<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
												Auto-Renew
											</span>
											<span className="text-sm font-medium text-slate-700">
												{subscription.cancelAtPeriodEnd ? (
													<span className="text-red-600 font-semibold">
														Canceling at period end
													</span>
												) : (
													<span className="text-green-600 font-semibold">
														Active
													</span>
												)}
											</span>
										</div>
										{subscription.cancellationRequestedAt && (
											<div className="md:col-span-3">
												<span className="text-xs text-slate-500 font-medium uppercase tracking-wider block">
													Cancellation Requested At
												</span>
												<span className="text-sm font-medium text-slate-700">
													{new Date(
														subscription.cancellationRequestedAt,
													).toLocaleString()}
												</span>
											</div>
										)}
									</div>

									<div className="space-y-4">
										<div className="flex items-center justify-between">
											<h3 className="text-lg font-medium text-slate-900">
												Billing Records
											</h3>
											<Button
												size="sm"
												variant="outline"
												onClick={handleOpenCreateRecord}
											>
												<Plus className="h-4 w-4 mr-2" />
												Add Billing Record
											</Button>
										</div>

										{billingRecordsLoading ? (
											<div className="text-center py-4 text-slate-500">
												Loading billing records...
											</div>
										) : billingRecords.length > 0 ? (
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>Description</TableHead>
														<TableHead>Period</TableHead>
														<TableHead>Total Amount</TableHead>
														<TableHead>Status</TableHead>
														<TableHead>Source</TableHead>
														<TableHead className="text-right">
															Actions
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{billingRecords.map((record) => (
														<TableRow key={record.id}>
															<TableCell className="font-medium">
																{record.description}
															</TableCell>
															<TableCell className="text-sm text-slate-600">
																{record.periodStart
																	? new Date(
																			record.periodStart,
																		).toLocaleDateString()
																	: ""}{" "}
																-{" "}
																{record.periodEnd
																	? new Date(
																			record.periodEnd,
																		).toLocaleDateString()
																	: ""}
															</TableCell>
															<TableCell className="font-semibold">
																{record.totalAmount} {record.currency}
															</TableCell>
															<TableCell>
																<Badge
																	variant={
																		record.status === "paid"
																			? "secondary"
																			: record.status === "open"
																				? "outline"
																				: "destructive"
																	}
																>
																	{record.status}
																</Badge>
															</TableCell>
															<TableCell className="text-xs font-mono">
																{record.source}
															</TableCell>
															<TableCell className="text-right space-x-2">
																{record.invoiceUrl && (
																	<Button
																		variant="ghost"
																		size="icon"
																		disabled={downloadingRecordId === record.id}
																		onClick={() => {
																			if (
																				record.invoiceUrl &&
																				record.invoiceUrl.startsWith("gs://")
																			) {
																				handleDownloadInvoice(record);
																			} else if (record.invoiceUrl) {
																				window.open(
																					record.invoiceUrl,
																					"_blank",
																				);
																			}
																		}}
																	>
																		{downloadingRecordId === record.id ? (
																			<Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
																		) : (
																			<Download className="w-4 h-4 text-slate-500 hover:text-slate-950" />
																		)}
																	</Button>
																)}
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleOpenEditRecord(record)}
																>
																	<Edit className="w-4 h-4 text-slate-500 hover:text-slate-950" />
																</Button>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => setRecordToDelete(record)}
																>
																	<Trash2 className="w-4 h-4 text-red-500 hover:text-red-750" />
																</Button>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										) : (
											<div className="text-center py-8 text-slate-500 border border-dashed rounded-lg">
												No billing records found
											</div>
										)}
									</div>
								</div>
							) : (
								<div className="text-center py-8 text-slate-500 border border-dashed rounded-lg">
									No subscription configured for this company.
								</div>
							)}

							{companySubscriptions.some(
								(candidate) => candidate.status === "canceled",
							) && (
								<div className="space-y-3">
									<h3 className="text-lg font-medium text-slate-900">
										Subscription History
									</h3>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Plan</TableHead>
												<TableHead>Price</TableHead>
												<TableHead>Canceled</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{companySubscriptions
												.filter((candidate) => candidate.status === "canceled")
												.map((candidate) => (
													<TableRow key={candidate.id}>
														<TableCell>{candidate.planName}</TableCell>
														<TableCell>
															{candidate.price} {candidate.currency} /{" "}
															{candidate.billingInterval}
														</TableCell>
														<TableCell>
															{candidate.canceledAt
																? new Date(
																		candidate.canceledAt,
																	).toLocaleString()
																: "Unknown"}
														</TableCell>
													</TableRow>
												))}
										</TableBody>
									</Table>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			{isEditing && (
				<div className="admin-company-savebar flex justify-end gap-2">
					<Button variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button onClick={handleSave}>Save Company Changes</Button>
				</div>
			)}

			{/* Create/Edit Subscription Dialog */}
			<Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
				<DialogContent className="admin-editor-dialog admin-editor-dialog--compact">
					<DialogHeader>
						<DialogTitle>
							{isEditingSub ? "Edit Subscription" : "Create Subscription"}
						</DialogTitle>
					</DialogHeader>
					<div className="admin-editor-body space-y-4">
						<div className="space-y-2">
							<Label>Plan Name</Label>
							<Input
								value={subPlanName}
								onChange={(e) => setSubPlanName(e.target.value)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Price</Label>
								<Input
									type="number"
									value={subPrice}
									onChange={(e) => setSubPrice(Number(e.target.value))}
								/>
							</div>
							<div className="space-y-2">
								<Label>Currency</Label>
								<Select
									value={subCurrency}
									onValueChange={(value: BillingCurrency) =>
										setSubCurrency(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="ILS">ILS</SelectItem>
										<SelectItem value="USD">USD</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Billing Interval</Label>
								<Select
									value={subBillingInterval}
									onValueChange={(value: Subscription["billingInterval"]) =>
										setSubBillingInterval(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="monthly">Monthly</SelectItem>
										<SelectItem value="yearly">Yearly</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Status</Label>
								<Select
									value={subStatus}
									onValueChange={(value: Subscription["status"]) =>
										setSubStatus(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">Active</SelectItem>
										<SelectItem value="trial">Trial</SelectItem>
										<SelectItem value="past_due">Past Due</SelectItem>
										<SelectItem value="canceled">Canceled</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="space-y-2">
							<Label>Next Billing Date</Label>
							<Input
								type="date"
								value={subNextBillingDate}
								onChange={(e) => setSubNextBillingDate(e.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label>Billing Provider</Label>
							<Input
								value={subBillingProvider}
								onChange={(e) => setSubBillingProvider(e.target.value)}
							/>
						</div>
						<div className="flex items-center gap-2 pt-2">
							<input
								id="subCancelAtPeriodEnd"
								type="checkbox"
								checked={subCancelAtPeriodEnd}
								onChange={(e) => setSubCancelAtPeriodEnd(e.target.checked)}
								className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
							/>
							<Label htmlFor="subCancelAtPeriodEnd" className="cursor-pointer">
								Cancel at Period End
							</Label>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowSubDialog(false)}>
							Cancel
						</Button>
						<Button onClick={handleSaveSub}>Save Subscription</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Create/Edit Billing Record Dialog */}
			<Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
				<DialogContent className="admin-editor-dialog admin-editor-dialog--compact">
					<DialogHeader>
						<DialogTitle>
							{editingRecord ? "Edit Billing Record" : "Add Billing Record"}
						</DialogTitle>
					</DialogHeader>
					<div className="admin-editor-body space-y-4">
						<div className="space-y-2">
							<Label>Description</Label>
							<Input
								value={recDescription}
								onChange={(e) => setRecDescription(e.target.value)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Period Start</Label>
								<Input
									type="date"
									value={recPeriodStart}
									onChange={(e) => setRecPeriodStart(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label>Period End</Label>
								<Input
									type="date"
									value={recPeriodEnd}
									onChange={(e) => setRecPeriodEnd(e.target.value)}
								/>
							</div>
						</div>
						<div className="grid grid-cols-3 gap-2">
							<div className="space-y-2">
								<Label>Amount</Label>
								<Input
									type="number"
									value={recAmount}
									onChange={(e) =>
										handleRecAmountChange(Number(e.target.value))
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>Tax</Label>
								<Input
									type="number"
									value={recTaxAmount}
									onChange={(e) => handleRecTaxChange(Number(e.target.value))}
								/>
							</div>
							<div className="space-y-2">
								<Label>Total Amount</Label>
								<Input
									type="number"
									value={recTotalAmount}
									disabled
									className="bg-slate-50"
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Currency</Label>
								<Select
									value={recCurrency}
									onValueChange={(value: BillingCurrency) =>
										setRecCurrency(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="ILS">ILS</SelectItem>
										<SelectItem value="USD">USD</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Status</Label>
								<Select
									value={recStatus}
									onValueChange={(value: BillingRecord["status"]) =>
										setRecStatus(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="open">Open</SelectItem>
										<SelectItem value="paid">Paid</SelectItem>
										<SelectItem value="failed">Failed</SelectItem>
										<SelectItem value="void">Void</SelectItem>
										<SelectItem value="refunded">Refunded</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Invoice Number</Label>
								<Input
									value={recInvoiceNumber}
									onChange={(e) => setRecInvoiceNumber(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label>Invoice URL</Label>
								<Input
									value={recInvoiceUrl}
									onChange={(e) => setRecInvoiceUrl(e.target.value)}
								/>
							</div>
						</div>

						{recStatus === "paid" && (
							<div className="p-3 bg-slate-50 rounded-lg border space-y-3">
								<h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
									Payment Details
								</h4>
								<div className="space-y-2">
									<Label>Paid At</Label>
									<Input
										type="date"
										value={recPaymentPaidAt}
										onChange={(e) => setRecPaymentPaidAt(e.target.value)}
									/>
								</div>
								<div className="grid grid-cols-2 gap-2">
									<div className="space-y-2">
										<Label>Payment Method</Label>
										<Select
											value={recPaymentMethod}
											onValueChange={(
												value: NonNullable<BillingRecord["payment"]>["method"],
											) => setRecPaymentMethod(value)}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="manual">Manual</SelectItem>
												<SelectItem value="credit_card">Credit Card</SelectItem>
												<SelectItem value="bank_transfer">
													Bank Transfer
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<Label>Payment Provider</Label>
										<Select
											value={recPaymentProvider}
											onValueChange={(
												value: NonNullable<
													BillingRecord["payment"]
												>["provider"],
											) => setRecPaymentProvider(value)}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="manual">Manual</SelectItem>
												<SelectItem value="grow">Grow</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>
								<div className="space-y-2">
									<Label>External Payment ID</Label>
									<Input
										value={recPaymentExternalId}
										onChange={(e) => setRecPaymentExternalId(e.target.value)}
									/>
								</div>
							</div>
						)}

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Source</Label>
								<Select
									value={recSource}
									onValueChange={(value: BillingRecord["source"]) =>
										setRecSource(value)
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="manual">Manual</SelectItem>
										<SelectItem value="grow">Grow</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
					<DialogFooter className="pt-4">
						<Button
							variant="outline"
							onClick={() => setShowRecordDialog(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleSaveRecord}>Save Record</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Confirm Delete Billing Record Alert Dialog */}
			<AlertDialog
				open={!!recordToDelete}
				onOpenChange={(open) => !open && setRecordToDelete(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Are you sure you want to delete this billing record?
						</AlertDialogTitle>
						<AlertDialogDescription>
							This action cannot be undone. This will permanently delete the
							billing record "{recordToDelete?.description}" from Firestore.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-600 hover:bg-red-700"
							onClick={handleDeleteRecord}
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Add SIP User Dialog */}
			<Dialog open={isAddSipUserOpen} onOpenChange={setIsAddSipUserOpen}>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Shield className="h-5 w-5 text-indigo-600" />
							Add SIP User / Operator
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label htmlFor="sip-username">SIP Username</Label>
							<Input
								id="sip-username"
								value={newSipUsername}
								onChange={(e) => setNewSipUsername(e.target.value)}
								placeholder="e.g. danj2 or operator1"
								className="font-mono text-sm"
							/>
							<p className="text-xs text-slate-500">
								Alphanumeric characters, dots, underscores, or hyphens.
							</p>
						</div>
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label htmlFor="sip-password">SIP Password</Label>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="h-7 text-xs text-indigo-600 px-2"
									onClick={generateStrongPassword}
								>
									<Key className="h-3 w-3 mr-1" />
									Generate Strong
								</Button>
							</div>
							<div className="flex gap-2">
								<Input
									id="sip-password"
									value={newSipPassword}
									onChange={(e) => setNewSipPassword(e.target.value)}
									placeholder="Password (minimum 12 chars)"
									className="font-mono text-sm"
								/>
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={() => {
										if (newSipPassword) {
											navigator.clipboard.writeText(newSipPassword);
											toast.success("Password copied to clipboard");
										}
									}}
									title="Copy Password"
								>
									<Copy className="h-4 w-4" />
								</Button>
							</div>
							<p className="text-xs text-slate-500">
								Twilio requires passwords to be at least 12 characters with
								upper, lower, numbers, and symbols.
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsAddSipUserOpen(false)}
							disabled={isSubmittingSipUser}
						>
							Cancel
						</Button>
						<Button
							onClick={handleCreateSipUser}
							disabled={
								isSubmittingSipUser || !newSipUsername.trim() || !newSipPassword
							}
						>
							{isSubmittingSipUser ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Creating...
								</>
							) : (
								"Create in Twilio"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete SIP User Alert Dialog */}
			<AlertDialog
				open={!!deletingSipCredential}
				onOpenChange={(open) => !open && setDeletingSipCredential(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Revoke SIP User: {deletingSipCredential?.username}?
						</AlertDialogTitle>
						<AlertDialogDescription>
							This will permanently remove the SIP credential from Twilio and
							revoke the operator&apos;s ability to make outbound SIP calls.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingSipUser}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-red-600 hover:bg-red-700"
							onClick={handleDeleteSipUser}
							disabled={isDeletingSipUser}
						>
							{isDeletingSipUser ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Deleting...
								</>
							) : (
								"Delete Credential"
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
